<template>
  <div class="space-y-10">
    <img
      v-if="brandSettings.logos.rectangle"
      :src="brandSettings.logos.rectangle.url"
      alt=""
      class="mx-auto w-52"
    >
    <template v-if="stepper.isCurrent('select-address')">
      <div class="space-y-base">
        <SpectreHeading
          as="h2"
          size="2xl"
          class="titles-branding text-center"
          :style="titlesStyling"
        >
          {{ instantValuation.content.title }}
        </SpectreHeading>
        <p
          class="body-text-branding text-center font-medium"
          :style="bodyTextStyling"
        >
          {{ instantValuation.content.description }}
        </p>
      </div>
      <div class="space-y-base sm:w-2/3 sm:mx-auto">
        <SpectreField class="hidden sm:block">
          <SpectreLabel class="sr-only">
            Address
          </SpectreLabel>
          <SpectreInputAddressSearch
            v-model="selectedProperty"
            :config="{
              hostUrl: page.props.config.kraken.search_url,
              apiKey: page.props.config.kraken.api_key,
            }"
            :leading-icon="faSearch"
            placeholder="Find your address"
            :initial-search-query="postcode"
          />
        </SpectreField>
        <button
          type="button"
          :class="[
            'rounded-md inline-flex items-center bg-white border-default-300 border w-full px-3 py-2.5 text-left gap-x-2 text-default-500',
            'sm:hidden',
          ]"
          @click="toggleShowModal(true)"
        >
          <FontAwesomeIcon
            :icon="faSearch"
            class="size-4 text-default-400"
            aria-hidden="true"
          />
          <span v-if="!address && !selectedProperty">
            Find your address
          </span>
          <span
            v-else
            class="text-default-900 line-clamp-1"
          >
            {{ address }}
          </span>
        </button>
        <SpectreButton
          :class="selectedProperty && 'insights-button'"
          :style="primaryButtonStyles"
          :disabled="!selectedProperty"
          block
          @click="onClick"
        >
          Continue
        </SpectreButton>
      </div>
    </template>
    <template v-else-if="stepper.isCurrent('enter-details')">
      <form
        class="md:w-5/6 md:mx-auto space-y-10"
        @submit.prevent="form.post(`${domain}/instant-valuations/${instantValuation.id}/leads/create`)"
      >
        <div class="space-y-base text-center">
          <button
            class="text-xs text-default-700 underline relative"
            type="button"
            @click="goBack"
          >
            <SpectreTouchTarget />
            Edit address
          </button>
          <h2 class="text-2xl font-bold">
            {{ address }}
          </h2>
          <p class="font-medium text-default-700">
            Your report is nearly done! Enter your details to view it instantly.
          </p>
        </div>
        <div class="grid md:grid-cols-2 gap-6">
          <SpectreField v-if="includeField('name')">
            <SpectreLabel class="sr-only">
              First name
            </SpectreLabel>
            <SpectreInput
              v-model="form.first_name"
              autocomplete="given-name"
              placeholder="First name"
              :required="fieldRequired('name')"
              :invalid="!!form.errors.first_name"
            />
            <SpectreErrorMessage v-if="form.errors.first_name">
              {{ form.errors.first_name }}
            </SpectreErrorMessage>
          </SpectreField>
          <SpectreField v-if="includeField('name')">
            <SpectreLabel class="sr-only">
              Last name
            </SpectreLabel>
            <SpectreInput
              v-model="form.last_name"
              autocomplete="family-name"
              placeholder="Last name"
              :required="fieldRequired('name')"
              :invalid="!!form.errors.last_name"
              inputmode="email"
            />
            <SpectreErrorMessage v-if="form.errors.last_name">
              {{ form.errors.last_name }}
            </SpectreErrorMessage>
          </SpectreField>
          <SpectreField class="sm:col-span-2">
            <SpectreLabel class="sr-only">
              Email
            </SpectreLabel>
            <SpectreInput
              v-model="form.email"
              placeholder="Email address"
              autocomplete="email"
              required
              :invalid="!!page.props.errors.email"
            />
            <SpectreErrorMessage v-if="form.errors.email">
              {{ form.errors.email }}
            </SpectreErrorMessage>
          </SpectreField>
          <SpectreField
            v-if="includeField('contactNumber')"
            class="sm:col-span-2"
          >
            <SpectreLabel class="sr-only">
              Contact number
            </SpectreLabel>
            <SpectreInput
              v-model="form.contact_number"
              placeholder="Contact number"
              type="tel"
              autocomplete="tel"
              :required="fieldRequired('contactNumber')"
              :invalid="!!form.errors.contact_number"
              inputmode="tel"
            />
            <SpectreErrorMessage v-if="form.errors.first_name">
              {{ form.errors.first_name }}
            </SpectreErrorMessage>
          </SpectreField>
          <SpectreField class="sm:col-span-2 flex gap-x-3">
            <SpectreCheckbox v-model="form.has_marketing_consent" />
            <SpectreLabel>
              I'd like to receive updates on my property and other related services
            </SpectreLabel>
          </SpectreField>
        </div>
        <div>
          <SpectreButton
            block
            :class="!isDisabled && !form.processing && 'insights-button'"
            :style="primaryButtonStyles"
            size="xl"
            :loading="form.processing"
            :disabled="isDisabled"
          >
            View report
          </SpectreButton>
        </div>
        <p class="text-default-700 text-xs text-center">
          By submitting, you agree to the <a
            href="https://street.co.uk/terms"
            class="underline outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            target="_blank"
          >terms and conditions</a> and our <a
            href="https://street.co.uk/terms"
            class="underline outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            target="_blank"
          >privacy policy</a>.
        </p>
      </form>
    </template>
  </div>
  <SpectreAddressSearchModal
    :config="{
      hostUrl: page.props.config.kraken.search_url,
      apiKey: page.props.config.kraken.api_key,
    }"
    :open="showModal"
    @close="toggleShowModal(false)"
    @update:model-value="(property: string) => {
      selectedProperty = property;
      onClick();
    }"
  />
</template>

<script lang="ts" setup>
import {
  SpectreAddressSearchModal,
  SpectreButton,
  SpectreCheckbox,
  SpectreErrorMessage,
  SpectreField, SpectreHeading,
  SpectreInput,
  SpectreInputAddressSearch,
  SpectreLabel,
  SpectreTouchTarget,
} from '@agentsoftware/spectre-ui';
import { router, useForm, usePage } from '@inertiajs/vue3';
import { computed, onMounted, ref } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSearch } from '@awesome.me/kit-644ec404bf/icons/classic/light';
import { useStepper, useToggle } from '@vueuse/core';
import BrandSettingsData = App.Data.Settings.BrandSettingsData;
import InstantValuationData = App.Data.InstantValuation.InstantValuationData;
import CustomDomainData = App.Data.CustomDomainData;
import InstantValuationContentData = App.Data.InstantValuation.InstantValuationContentData;

const props = defineProps<{
  address?: string;
  brandSettings: BrandSettingsData;
  customDomain?: CustomDomainData;
  instantValuation: InstantValuationData;
  instantValuationContent: InstantValuationContentData;
  streetGroupPropertyId?: string;
  postcode?: string;
}>();

const page = usePage();

const selectedProperty = ref<string | undefined>(props.streetGroupPropertyId);

const [showModal, toggleShowModal] = useToggle<boolean>(false);

const stepper = useStepper([
  'select-address',
  'enter-details',
]);

const form = useForm({
  street_group_property_id: props.streetGroupPropertyId,
  first_name: '',
  last_name: '',
  email: '',
  contact_number: '',
  has_marketing_consent: false,
});

const domain = computed(() => (props.customDomain && !props.instantValuation.shortUrl?.url.includes(page.props.app.url) ? `https://${props.customDomain.url}` : page.props.app.url));

const includeField = (fieldName: 'name' | 'contactNumber') => props.instantValuationContent.fields?.find((field) => field.id === fieldName)?.display ?? false;
const fieldRequired = (fieldName: 'name' | 'contactNumber') => props.instantValuationContent.fields?.find((field) => field.id === fieldName)?.required ?? false;

onMounted(() => {
  if (window.location.hash === '#contact-details' && props.streetGroupPropertyId) {
    stepper.goTo('enter-details');
  }
});

const onClick = () => {
  router.get('#contact-details', {
    street_group_property_id: selectedProperty.value,
  }, {
    onSuccess: () => {
      toggleShowModal(false);
    },
    preserveState: false,
  });
};

const goBack = () => {
  router.get('', {}, {
    preserveState: false,
  });
};

const titlesStyling = computed(() => ({
  '--titlesFontFamily': props.brandSettings.text.heading.fontFamily,
  '--titlesFontWeight': props.brandSettings.text.heading.fontWeight,
  '--titlesColour': props.brandSettings.text.heading.colour,
}));

const bodyTextStyling = computed(() => ({
  '--bodyTextFontFamily': props.brandSettings.text.body.fontFamily,
  '--bodyTextFontWeight': props.brandSettings.text.body.fontWeight,
  '--bodyTextColour': props.brandSettings.text.body.colour,
}));

const primaryButtonStyles = computed(() => ({
  '--primaryButtonFontFamily': `${props.brandSettings.buttons.primary.fontFamily}`,
  '--primaryButtonFontWeight': `${props.brandSettings.buttons.primary.fontWeight}`,
  '--primaryButtonTextColour': `${props.brandSettings.buttons.primary.colour}`,
  '--primaryButtonTextHoverColour': `${props.brandSettings.buttons.primary.hoverColour}`,
  '--primaryButtonBackgroundColour': `${props.brandSettings.buttons.primary.backgroundColour}`,
  '--primaryButtonBackgroundHoverColour': `${props.brandSettings.buttons.primary.backgroundHoverColour}`,
}));

const isDisabled = computed(() => !form.email || (fieldRequired('name') && (!form.first_name || !form.last_name)) || (fieldRequired('contactNumber') && !form.contact_number));
</script>

<style scoped>
.titles-branding {
  font-family: var(--titlesFontFamily);
  font-weight: var(--titlesFontWeight);
  color: var(--titlesColour);
}

.body-text-branding {
  font-family: var(--bodyTextFontFamily);
  font-weight: var(--bodyTextFontWeight);
  color: var(--bodyTextColour);
}

.insights-button {
  font-family: var(--primaryButtonFontFamily);
  font-weight: var(--primaryButtonFontWeight);
  color: var(--primaryButtonTextColour);
  background-color: var(--primaryButtonBackgroundColour);
  border-color: var(--primaryButtonBackgroundColour);
}
.insights-button:hover {
  color: var(--primaryButtonTextHoverColour);
  background-color: var(--primaryButtonBackgroundHoverColour);
  border-color: var(--primaryButtonBackgroundHoverColour);
}
</style>
